import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import classNames from 'classnames';
import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { TextInput } from '../TextInput/TextInput';
import { useClickAway } from '../hooks/useClickAway';
import { useDebounce } from '../hooks/useDebounce';
import { useStableCallback } from '../hooks/useStableCallback';
import { ManagedDomEventListeners } from '../utils/dom/ManagedDomEventListeners';
import {
  ARROW_DOWN,
  ARROW_UP,
  BACKSPACE,
  DELETE,
  ENTER,
  ESCAPE,
  SPACE_KEY,
  TAB,
} from '../utils/keyboard/keyboardKeys';
import { getAutocompleteClassNames } from './AutocompleteInput.styles';
import {
  type AutocompleteInputRecordProps,
  type AutocompleteInputStringProps,
  type BaseAutocompleteInputProps,
  type SearchResult,
  isStringAutocompleteOption,
} from './AutocompleteInput.types';

const DEFAULT_PAGE = 0;

const AutocompleteInputBase = <TOption extends string | Record<string, any>>({
  className,
  getOptionLabel,
  isOptionEqualToValue,
  menuLabel,
  renderMenuHeader,
  name,
  onBlur,
  onChange,
  onClear,
  onOptionSelect,
  onSearch,
  options,
  value,
  inputId,
  hideClearButton,
  hideMenuWhenEmpty,
  keepMenuOpenOnSelect,
  ...props
}: BaseAutocompleteInputProps<TOption>) => {
  const autocompleteClassNames = getAutocompleteClassNames();

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuOpen, setMenuOpen] = useState(false);

  const containerRef = useClickAway<HTMLDivElement>(() => setMenuOpen(false));

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLInputElement>(null);

  const pagerRef = useRef<SearchResult<TOption>>({
    currentPage: DEFAULT_PAGE,
    totalRowsCount: null,
    totalPageCount: null,
    paginationLimit: 0,
    content: [],
  });

  const debounceSearch = useDebounce(150);

  // fallback function if getOptionLabel is not provided
  const getLabel = useStableCallback((option: TOption): string => {
    if (isStringAutocompleteOption(option)) {
      return option; // return string itself
    }
    if (getOptionLabel) {
      return getOptionLabel(option); // use provided function if available
    }
    stumble('getOptionLabel prop is missing for object options.');
    return ''; // return empty string as a safe fallback
  });

  // fallback function if isOptionEqualToValue is not provided
  const isOptionEqualToValueInternal = useStableCallback(
    (option: TOption, value: string): boolean => {
      if (isStringAutocompleteOption(option)) {
        return option === value;
      }
      if (isOptionEqualToValue) {
        return isOptionEqualToValue(option, value); // use provided function if available
      }
      stumble('isOptionEqualToValue prop is missing for object options.');
      return false; // return false as a safe fallback
    },
  );

  const fetchFilteredOptions = useStableCallback(async (page: number) => {
    pagerRef.current = await onSearch({
      nextPageNumber: page,
      inputValue: value,
    });
    return pagerRef.current;
  });

  const handleClick = useStableCallback(async () => {
    if (!props.disabled) {
      setMenuOpen((prevState) => !prevState);
      await fetchFilteredOptions(DEFAULT_PAGE);
    }
  });

  const handleClear = useStableCallback(() => {
    setMenuOpen(false);
    onClear?.();
  });

  const handleChange = useStableCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);

    debounceSearch(async () => {
      if (menuRef.current) {
        // scroll to top to show new results
        menuRef.current.scrollTo(0, 0);
      }
      await fetchFilteredOptions(DEFAULT_PAGE);
    });
  });

  useEffect(() => {
    if (props.disabled) {
      // close menu if dropdown gets disabled
      setMenuOpen(false);
    }
  }, [props.disabled]);

  useEffect(() => {
    if (options?.length) {
      // reset highlighted index, allowing to select the first option via keyboard when options change and user did not choose any option yet
      // https://ipfabric.atlassian.net/browse/NIM-20936
      setHighlightedIndex(0);
    }
  }, [options]);

  useEffect(() => {
    if (!menuOpen || !menuRef.current) {
      return;
    }

    const menu = menuRef.current;

    // trigger search when scrolling to the bottom of the menu
    const handleMenuScroll = async () => {
      if (menu.scrollHeight - menu.scrollTop - menu.clientHeight <= 1) {
        await fetchFilteredOptions(pagerRef.current.currentPage + 1);
      }
    };

    const scrollEventListener = new ManagedDomEventListeners();
    scrollEventListener.add([menu, 'scroll', handleMenuScroll]);

    return () => {
      scrollEventListener.remove();
    };
  });

  const selectOption = useStableCallback((option: TOption) => {
    if (!keepMenuOpenOnSelect) {
      setMenuOpen(false);
    }
    isStringAutocompleteOption(option)
      ? onChange(option)
      : onOptionSelect?.(option);
  });

  const handleBlur = useStableCallback(() => {
    // clear searched text and close dropdown menu with lost focus
    setMenuOpen(false);
    setHighlightedIndex(-1);
  });

  const handleKeyDown = useStableCallback(
    async (e: KeyboardEvent<HTMLDivElement>) => {
      if (!isAllowedKey(e.key)) {
        return;
      }

      if (!menuOpen && e.key !== ESCAPE && e.key !== TAB) {
        await openMenuWithFilteredOptions();
        return;
      }

      switch (e.key) {
        case ARROW_DOWN:
          e.preventDefault();
          await handleArrowDown();
          break;

        case ARROW_UP:
          e.preventDefault();
          handleArrowUp();
          break;

        case ENTER:
          e.preventDefault();
          if (options?.length && highlightedIndex !== -1) {
            selectHighlightedOption();
          }

          break;

        case ESCAPE:
          e.preventDefault();
          closeMenu();
          break;

        default:
          // reset highlighted index when typing
          setHighlightedIndex(-1);
      }
    },
  );

  // extracted helper functions

  const isAllowedKey = (key: string) => {
    const isNavigationKey = key === ARROW_DOWN || key === ARROW_UP;
    const isPrintableCharacter = /^[^\p{Cc}\p{Cn}\p{Cs}]$/u.test(key);
    return (
      [SPACE_KEY, ENTER, TAB, BACKSPACE, DELETE, ESCAPE].includes(key) ||
      isNavigationKey ||
      isPrintableCharacter
    );
  };

  const scrollToItem = (index: number) => {
    setTimeout(() =>
      itemRefs.current[index]?.scrollIntoView({ block: 'nearest' }),
    );
  };

  const selectHighlightedOption = () => {
    const itemToHighlight = options?.[highlightedIndex];

    if (itemToHighlight === undefined) {
      stumble(
        `Item at index ${highlightedIndex} is missing and cannot be highlighted.`,
      );
      return;
    }

    selectOption(itemToHighlight);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setHighlightedIndex(-1);
  };

  const openMenuWithFilteredOptions = async () => {
    const { content } = await fetchFilteredOptions(DEFAULT_PAGE);

    const index =
      content?.findIndex((item) => isOptionEqualToValueInternal(item, value)) ??
      0;
    const newIndex = Math.max(index, 0);

    setMenuOpen(true);
    setHighlightedIndex(newIndex);
    scrollToItem(newIndex);
  };

  const handleArrowDown = async () => {
    if (props.loading || options === null) {
      return;
    }

    let nextIndex = highlightedIndex + 1;
    if (nextIndex >= options.length) {
      await fetchFilteredOptions(pagerRef.current.currentPage + 1);
      if (
        pagerRef.current.totalPageCount !== null &&
        pagerRef.current.currentPage >= pagerRef.current.totalPageCount
      ) {
        nextIndex = 0; // cycle back to the first option
      }
    }

    setHighlightedIndex(nextIndex);
    scrollToItem(nextIndex);
  };

  const handleArrowUp = () => {
    if (options) {
      const nextIndex =
        highlightedIndex > 0 ? highlightedIndex - 1 : options.length - 1;
      setHighlightedIndex(nextIndex);
      scrollToItem(nextIndex);
    }
  };

  // A renderMenuHeader is still content, so keep the panel when one is given.
  const hideEmptyMenu =
    hideMenuWhenEmpty && !renderMenuHeader && !options?.length;

  const renderOptions = () => {
    if (options === null) {
      return (
        <div className={autocompleteClassNames.option}>Type to search</div>
      );
    }

    if (options.length > 0) {
      return options.map((option, index) => {
        const label = getLabel(option);
        const key = props.getOptionKey
          ? props.getOptionKey(option, index)
          : label;
        return (
          <div
            key={key}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            tabIndex={-1}
            onClick={() => {
              setHighlightedIndex(-1);
              selectOption(option);
            }}
            onMouseEnter={() => setHighlightedIndex(index)}
            className={classNames(
              autocompleteClassNames.option,
              'cursor-pointer',
              index === highlightedIndex && 'bg-state-hover',
              isOptionEqualToValueInternal(option, value) &&
                'bg-status-selected text-status-selected',
            )}
          >
            {props.renderOption ? props.renderOption(option) : label}
          </div>
        );
      });
    }

    return <div className={autocompleteClassNames.option}>No results</div>;
  };

  return (
    <div ref={containerRef}>
      <div className='relative'>
        <TextInput
          className={className}
          hasDropdown
          dropdownOpened={menuOpen ? 'bottom' : null}
          inputRef={inputRef}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onClear={hideClearButton ? undefined : handleClear}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          type='text'
          value={value}
          id={inputId}
          {...props}
        />
        {menuOpen && !hideEmptyMenu && (
          <div
            className={autocompleteClassNames.optionsContainer}
            onMouseDown={(e) => e.preventDefault()} // prevent focus shift
            ref={menuRef}
          >
            {renderMenuHeader ? (
              <div onMouseDown={(event) => event.preventDefault()}>
                {renderMenuHeader}
              </div>
            ) : null}
            {menuLabel && options !== null && (
              <div
                className={classNames(
                  autocompleteClassNames.option,
                  'text-xs font-semibold',
                )}
              >
                {menuLabel}
              </div>
            )}
            {renderOptions()}
          </div>
        )}
      </div>
    </div>
  );
};

const AutocompleteInputString: FC<AutocompleteInputStringProps> =
  AutocompleteInputBase;

const AutocompleteInputRecord = <TOption extends Record<string, any>>(
  props: AutocompleteInputRecordProps<TOption>,
) => <AutocompleteInputBase {...props} />;

export const AutocompleteInput = {
  String: AutocompleteInputString,
  Record: AutocompleteInputRecord,
};
